import { useState } from 'react'
import { FileText, Share2, Lock, Zap, Users, BarChart3, CheckCircle2, ArrowRight } from 'lucide-react'

export default function DocumentManagementPage() {
  const [stats] = useState({
    totalDocuments: 5247,
    storageUsed: '487 GB / 1 TB',
    activeUsers: 234,
    sharesThisMonth: 1456,
  })

  const features = [
    {
      title: '📄 Document Storage',
      description: 'Unlimited cloud storage with advanced organization and tagging',
      icon: FileText,
      color: 'from-blue-600 to-blue-500',
      stats: '5,247 Documents',
      path: '/storage'
    },
    {
      title: '👥 Team Collaboration',
      description: 'Real-time collaboration with comments, mentions, and discussions',
      icon: Users,
      color: 'from-purple-600 to-purple-500',
      stats: '234 Active Users',
      path: '/collaboration'
    },
    {
      title: '🔐 Access Control',
      description: 'Granular permissions with role-based access and sharing controls',
      icon: Lock,
      color: 'from-red-600 to-red-500',
      stats: 'AES-256 Encryption',
      path: '/access-control'
    },
    {
      title: '📤 File Sharing',
      description: 'Secure file sharing with expiring links and download tracking',
      icon: Share2,
      color: 'from-green-600 to-green-500',
      stats: '1,456 Shares/Month',
      path: '/file-sharing'
    },
    {
      title: '⚡ Sync & Backup',
      description: 'Automatic sync across devices with real-time backup',
      icon: Zap,
      color: 'from-yellow-600 to-yellow-500',
      stats: 'Real-time Sync',
      path: '/sync-backup'
    },
    {
      title: '📊 Advanced Search',
      description: 'Full-text search with OCR for images and PDFs',
      icon: BarChart3,
      color: 'from-cyan-600 to-cyan-500',
      stats: 'Instant Results',
      path: '/search'
    },
    {
      title: '✓ Compliance',
      description: 'GDPR, HIPAA, and SOC2 compliance with audit trails',
      icon: CheckCircle2,
      color: 'from-teal-600 to-teal-500',
      stats: 'Fully Compliant',
      path: '/compliance'
    },
    {
      title: '🔄 Integrations',
      description: 'Connect with Slack, Teams, Google Workspace, and more',
      icon: Zap,
      color: 'from-indigo-600 to-indigo-500',
      stats: '50+ Integrations',
      path: '/integrations'
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 p-8 text-white shadow-2xl border border-orange-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <FileText className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Document Management</h1>
                </div>
                <p className="text-orange-100 text-lg font-semibold">Enterprise Document Storage & Collaboration</p>
                <p className="text-orange-200 text-sm mt-1">Cloud Storage • Team Collaboration • Version Control • Secure Sharing</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.totalDocuments}</div>
                <div className="text-sm text-orange-100">Total Documents</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Documents', value: stats.totalDocuments, icon: FileText, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
            { label: 'Storage Used', value: stats.storageUsed, icon: Zap, color: 'from-orange-600 to-orange-500', textColor: 'text-orange-400' },
            { label: 'Active Users', value: stats.activeUsers, icon: Users, color: 'from-purple-600 to-purple-500', textColor: 'text-purple-400' },
            { label: 'Shares This Month', value: stats.sharesThisMonth, icon: Share2, color: 'from-green-600 to-green-500', textColor: 'text-green-400' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className={`${stat.textColor} text-3xl font-black mt-3`}>{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Features Grid */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <FileText className="w-7 h-7 text-orange-400" />
            <span>Document Management Features</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div
                  key={idx}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all cursor-pointer shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1"
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>

                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="font-bold text-white mb-2 text-lg">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-4 h-4 text-blue-400" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Storage & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Storage Breakdown */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Zap className="w-5 h-5 text-orange-400" />
              <span>Storage Breakdown</span>
            </h3>
            <div className="space-y-3">
              {[
                { type: 'Documents', size: '245 GB', percentage: 50 },
                { type: 'Images & Media', size: '156 GB', percentage: 32 },
                { type: 'Archives', size: '65 GB', percentage: 13 },
                { type: 'Backups', size: '21 GB', percentage: 5 },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-4 border border-orange-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-semibold text-sm">{item.type}</p>
                    <span className="text-orange-400 font-bold">{item.size}</span>
                  </div>
                  <div className="w-full bg-slate-600 rounded-full h-2">
                    <div className="h-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-full" style={{width: `${item.percentage}%`}}></div>
                  </div>
                  <p className="text-blue-300 text-xs mt-1 font-semibold">{item.percentage}% of total</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Users className="w-5 h-5 text-purple-400" />
              <span>Recent Collaboration</span>
            </h3>
            <div className="space-y-3">
              {[
                { doc: 'Q2 Financial Report.pdf', user: 'Sarah Mitchell', action: 'Edited', time: '2 hours ago' },
                { doc: 'Project Timeline.xlsx', user: 'John Davis', action: 'Commented', time: '4 hours ago' },
                { doc: 'Design System v2.figma', user: 'Emma Wilson', action: 'Shared', time: '6 hours ago' },
                { doc: 'Meeting Notes - April.doc', user: 'Michael Chen', action: 'Viewed', time: '1 day ago' },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-purple-500/20">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white font-semibold text-sm">{item.doc}</p>
                    <span className="bg-purple-500/20 text-purple-300 text-xs font-bold px-2 py-0.5 rounded">
                      {item.action}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300 text-xs">{item.user}</span>
                    <span className="text-cyan-400 text-xs font-semibold">{item.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
